/**
 * 壁設置入力の状態機械 — 純粋JS（DOM / three.js 非依存、node でテスト可能）。
 *
 * マウス: ホバーでプレビュー、クリックで即設置（従来どおり）。
 * タッチ/ペン: 2段階確定式。
 *   1タップ目（溝）= 候補プレビュー → 同じスロット再タップ or confirm() で設置。
 *   別の溝タップ = 候補移動 / マス・盤外タップ = 候補解除。
 *
 * このモジュールは「どのスロットがタップされたか」(ピッキング済み) と
 * pointerType を受け取り、「何をすべきか」(place / preview / illegal / cell)
 * だけを返す。レイキャスト・isLegal・SE・描画は呼び出し側（main.js）の責務。
 * ゲームルールは一切持たない（legal フラグは呼び出し側が isLegal で判定して渡す）。
 */

/** タッチ扱いにする pointerType（ペンも指と同じく2段階確定にする）。 */
export function isTouchLike(pointerType) {
  return pointerType === 'touch' || pointerType === 'pen';
}

/** スロット同一判定（アンカー座標と向きが一致）。 */
export function sameSlot(a, b) {
  return (
    !!a &&
    !!b &&
    a.at.col === b.at.col &&
    a.at.row === b.at.row &&
    a.orientation === b.orientation
  );
}

/**
 * タップ/ドラッグ判別。pointerdown → pointerup の移動距離が閾値以下なら
 * タップとみなす。タッチは指ブレを考慮してマウスより閾値を広げる。
 *
 *   tracker.down(e);            // pointerdown
 *   const tap = tracker.up(e);  // pointerup: タップなら true
 *   tracker.cancel();           // pointercancel（OrbitControls が奪った等）
 *
 * e は { pointerType, clientX, clientY } を持てばよい（PointerEvent 互換）。
 */
export function createTapTracker({ mouseThreshold = 6, touchThreshold = 12 } = {}) {
  let down = null;
  return {
    down(e) {
      down = { x: e.clientX, y: e.clientY };
    },
    up(e) {
      if (!down) return false;
      const moved = Math.hypot(e.clientX - down.x, e.clientY - down.y);
      down = null;
      const threshold = isTouchLike(e.pointerType) ? touchThreshold : mouseThreshold;
      return moved <= threshold;
    },
    cancel() {
      down = null;
    },
  };
}

/**
 * 壁設置の candidate → confirm 状態機械。
 *
 * tap(pointerType, slot):
 *   slot = { at: {col,row}, orientation, legal } | null（溝上でないタップは null）
 *   返値（呼び出し側が実行するアクション）:
 *     { type: 'place', slot }    … 設置を試みる（マウス即設置 / タッチ2タップ目）
 *     { type: 'preview', slot }  … 候補プレビュー表示（タッチ1タップ目・候補移動）
 *     { type: 'illegal', slot }  … 不正スロットの確定操作: illegal SE のみ（候補維持）
 *     { type: 'cell', cleared }  … 溝以外のタップ: マス処理へ進む。cleared=候補を解除した
 *
 * confirm(): 確定ボタン（✓ 置く）。{ type: 'place'|'illegal'|'none' }
 * cancel(): 候補解除（✕ ボタン・能力モード移行・着手確定時）
 * reset():  cancel と同じ（対局リセット等、意図明示用）
 */
export function createWallInput() {
  let candidate = null;

  return {
    /** 現在の候補スロット（{at, orientation, legal} | null）。確定UIの表示判定用。 */
    get candidate() {
      return candidate;
    },

    tap(pointerType, slot) {
      // マウス: 候補という概念なし。溝なら即設置、そうでなければマス処理へ。
      if (!isTouchLike(pointerType)) {
        const cleared = candidate !== null; // 併用端末: タッチ候補は破棄
        candidate = null;
        return slot ? { type: 'place', slot } : { type: 'cell', cleared };
      }

      // タッチ/ペン
      if (!slot) {
        const cleared = candidate !== null;
        candidate = null;
        return { type: 'cell', cleared };
      }
      if (sameSlot(candidate, slot)) {
        // 同じスロットの再タップ = 確定操作。legal は今回タップ時の再判定を使う。
        if (slot.legal) {
          candidate = null;
          return { type: 'place', slot };
        }
        candidate = slot; // 赤プレビューは維持（最新の legal で更新）
        return { type: 'illegal', slot };
      }
      candidate = slot;
      return { type: 'preview', slot };
    },

    confirm() {
      if (!candidate) return { type: 'none' };
      if (!candidate.legal) return { type: 'illegal', slot: candidate };
      const slot = candidate;
      candidate = null;
      return { type: 'place', slot };
    },

    cancel() {
      candidate = null;
    },

    reset() {
      candidate = null;
    },
  };
}
